import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Text, Loader } from '@pingux/astro';
import { ValidationDashboardItem, ValidationStatus, ValidationRunStatus, ValidationRunResult } from '@app-types/tap-partner-vault';
import { validationRunsService } from '@services/validation-runs';

const resultBadgeStyle = (result: ValidationRunResult | ValidationRunStatus | ValidationStatus | null): React.CSSProperties => {
  const map: Record<string, { bg: string; color: string }> = {
    valid:          { bg: '#d1fae5', color: '#065f46' },
    invalid:        { bg: '#fee2e2', color: '#991b1b' },
    error:          { bg: '#fef3c7', color: '#92400e' },
    running:        { bg: '#dbeafe', color: '#1e40af' },
    completed:      { bg: '#d1fae5', color: '#065f46' },
    pending:        { bg: '#f3f4f6', color: '#6b7280' },
    not_applicable: { bg: '#f3f4f6', color: '#6b7280' },
  };
  const style = result ? map[result] : { bg: '#f3f4f6', color: '#6b7280' };
  return {
    display: 'inline-block',
    padding: '0.15rem 0.5rem',
    borderRadius: '9999px',
    fontSize: '0.7rem',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    backgroundColor: style?.bg ?? '#f3f4f6',
    color: style?.color ?? '#6b7280',
  };
};

const formatDate = (iso: string | null) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const ValidationDashboard: React.FC = () => {
  const [items, setItems] = useState<ValidationDashboardItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Track which credentials have a run in progress (credential_id -> run_id)
  const [runningRuns, setRunningRuns] = useState<Record<string, string>>({});
  // Track expanded rows (credential_id)
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // Detail data for expanded row
  const [screenshots, setScreenshots] = useState<string[]>([]);
  const [logContent, setLogContent] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  // Full-size screenshot modal
  const [fullScreenshot, setFullScreenshot] = useState<string | null>(null);

  const pollTimersRef = useRef<Record<string, ReturnType<typeof setInterval>>>({});

  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await validationRunsService.getDashboard();
      setItems(data.items);

      // Auto-poll for any that are currently running
      const running: Record<string, string> = {};
      for (const item of data.items) {
        if (item.last_run_status === 'running' && item.last_run_id) {
          running[item.id] = item.last_run_id;
        }
      }
      setRunningRuns(running);
    } catch {
      setError('Failed to load validation dashboard.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  // Polling effect for running runs
  useEffect(() => {
    const timers = pollTimersRef.current;

    // Start polling for new running runs
    for (const [credentialId, runId] of Object.entries(runningRuns)) {
      if (timers[credentialId]) continue;
      timers[credentialId] = setInterval(async () => {
        try {
          const run = await validationRunsService.getRun(runId);
          if (run.status !== 'running') {
            clearInterval(timers[credentialId]);
            delete timers[credentialId];
            setRunningRuns(prev => {
              const next = { ...prev };
              delete next[credentialId];
              return next;
            });
            // Refresh dashboard data
            loadDashboard();
          }
        } catch {
          clearInterval(timers[credentialId]);
          delete timers[credentialId];
          setRunningRuns(prev => {
            const next = { ...prev };
            delete next[credentialId];
            return next;
          });
        }
      }, 3000);
    }

    // Clean up timers for runs that are no longer in the running set
    for (const credentialId of Object.keys(timers)) {
      if (!runningRuns[credentialId]) {
        clearInterval(timers[credentialId]);
        delete timers[credentialId];
      }
    }

    return () => {
      for (const timer of Object.values(timers)) {
        clearInterval(timer);
      }
    };
  }, [runningRuns, loadDashboard]);

  // Cleanup screenshot URLs on unmount or collapse
  const cleanupScreenshots = useCallback(() => {
    for (const url of screenshots) {
      URL.revokeObjectURL(url);
    }
    setScreenshots([]);
    setLogContent(null);
  }, [screenshots]);

  const handleStartRun = async (credentialId: string) => {
    try {
      const run = await validationRunsService.startRun(credentialId);
      setRunningRuns(prev => ({ ...prev, [credentialId]: run.id }));
      // Update item inline to show running status
      setItems(prev => prev.map(item =>
        item.id === credentialId
          ? { ...item, last_run_id: run.id, last_run_status: 'running' as const, last_run_result: null, last_run_message: null, last_run_started_at: run.started_at, last_run_completed_at: null }
          : item
      ));
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      const message = axiosErr?.response?.data?.message || 'Failed to start validation run.';
      setError(message);
    }
  };

  const handleToggleExpand = async (item: ValidationDashboardItem) => {
    if (expandedId === item.id) {
      cleanupScreenshots();
      setExpandedId(null);
      return;
    }

    cleanupScreenshots();
    setExpandedId(item.id);

    if (!item.last_run_id) return;

    setDetailLoading(true);
    try {
      // Load screenshots (admin_account only) and log in parallel
      const isAdminAccount = item.credential_type === 'admin_account';
      const screenshotCount = isAdminAccount ? (item.last_run_screenshot_count ?? 0) : 0;
      const screenshotPromises = Array.from({ length: screenshotCount }, (_, i) =>
        validationRunsService.getScreenshot(item.last_run_id!, i)
      );

      const [screenshotUrls, log] = await Promise.all([
        Promise.all(screenshotPromises),
        validationRunsService.getLog(item.last_run_id).catch(() => null),
      ]);

      setScreenshots(screenshotUrls);
      setLogContent(log);
    } catch {
      // Partial failure is fine
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <Box sx={{ padding: '1.5rem 0' }}>
      <Box sx={{ marginBottom: '1.5rem' }}>
        <Text sx={{ fontSize: '1.5rem', fontWeight: '700', color: '#111827' }}>
          Validation Dashboard
        </Text>
        <Text sx={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem' }}>
          Monitor and trigger credential validation runs
        </Text>
      </Box>

      {error && (
        <Box sx={{ padding: '1rem', backgroundColor: '#fee2e2', borderRadius: '0.5rem', marginBottom: '1rem', borderLeft: '4px solid #dc2626' }}>
          <Text sx={{ color: '#991b1b' }}>{error}</Text>
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
          <Loader />
        </Box>
      ) : items.length === 0 ? (
        <Box sx={{ padding: '3rem', textAlign: 'center', backgroundColor: 'white', borderRadius: '0.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <Text sx={{ color: '#6b7280' }}>
            No credentials with validation enabled.
          </Text>
        </Box>
      ) : (
        <Box sx={{ backgroundColor: 'white', borderRadius: '0.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                {['Partner', 'Tenant', 'Type', 'Environment', 'Account', 'Status', 'Last Run', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const isRunning = !!runningRuns[item.id];
                const isExpanded = expandedId === item.id;

                return (
                  <React.Fragment key={item.id}>
                    {/* Row */}
                    <tr
                      style={{ borderBottom: '1px solid #f3f4f6', cursor: item.last_run_id ? 'pointer' : 'default', backgroundColor: isExpanded ? '#f9fafb' : undefined }}
                      onClick={() => item.last_run_id && handleToggleExpand(item)}
                    >
                      <td style={{ padding: '0.75rem 1rem', fontWeight: '500', color: '#111827' }}>{item.partner}</td>
                      <td style={{ padding: '0.75rem 1rem', color: '#374151' }}>{item.tenant_id}</td>
                      <td style={{ padding: '0.75rem 1rem', color: '#6b7280' }}>{item.credential_type.replace('_', ' ')}</td>
                      <td style={{ padding: '0.75rem 1rem', color: '#6b7280' }}>{item.environment}</td>
                      <td style={{ padding: '0.75rem 1rem', color: '#374151' }}>{item.account_name ?? '—'}</td>
                      <td style={{ padding: '0.75rem 1rem' }}>
                        {isRunning ? (
                          <span style={resultBadgeStyle('running')}>running</span>
                        ) : (
                          <span style={resultBadgeStyle(item.validation_status)}>{item.validation_status}</span>
                        )}
                      </td>
                      <td style={{ padding: '0.75rem 1rem', color: '#6b7280', whiteSpace: 'nowrap' }}>
                        {formatDate(item.last_run_started_at)}
                      </td>
                      <td style={{ padding: '0.75rem 1rem' }} onClick={e => e.stopPropagation()}>
                        <button
                          onClick={() => handleStartRun(item.id)}
                          disabled={isRunning}
                          style={{
                            padding: '0.3rem 0.75rem',
                            fontSize: '0.8rem',
                            fontWeight: '500',
                            color: isRunning ? '#9ca3af' : '#ffffff',
                            backgroundColor: isRunning ? '#e5e7eb' : '#2563eb',
                            border: 'none',
                            borderRadius: '0.375rem',
                            cursor: isRunning ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {isRunning ? 'Running...' : 'Validate'}
                        </button>
                      </td>
                    </tr>

                    {/* Expanded detail row */}
                    {isExpanded && (
                      <tr key={`${item.id}-detail`} style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td colSpan={8} style={{ padding: '1rem 1.5rem', backgroundColor: '#f9fafb' }}>
                          {detailLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', padding: '1rem' }}>
                              <Loader />
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                              {/* Run metadata */}
                              <Box sx={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
                                <Box>
                                  <Text sx={{ fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: '600' }}>Result</Text>
                                  <span style={resultBadgeStyle(item.last_run_result)}>
                                    {item.last_run_result ?? '—'}
                                  </span>
                                </Box>
                                <Box>
                                  <Text sx={{ fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: '600' }}>Started</Text>
                                  <Text sx={{ fontSize: '0.85rem', color: '#111827' }}>{formatDate(item.last_run_started_at)}</Text>
                                </Box>
                                <Box>
                                  <Text sx={{ fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: '600' }}>Completed</Text>
                                  <Text sx={{ fontSize: '0.85rem', color: '#111827' }}>{formatDate(item.last_run_completed_at)}</Text>
                                </Box>
                                {item.last_run_message && (
                                  <Box sx={{ flex: '1 1 100%' }}>
                                    <Text sx={{ fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: '600' }}>Message</Text>
                                    <Text sx={{ fontSize: '0.85rem', color: '#374151' }}>{item.last_run_message}</Text>
                                  </Box>
                                )}
                              </Box>

                              {/* Screenshots (admin_account only) */}
                              {item.credential_type === 'admin_account' && screenshots.length > 0 && (
                                <Box>
                                  <Text sx={{ fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: '600', marginBottom: '0.5rem' }}>
                                    Screenshots ({screenshots.length})
                                  </Text>
                                  <div style={{
                                    display: 'flex',
                                    gap: '0.5rem',
                                    overflowX: 'auto',
                                    paddingBottom: '0.5rem',
                                  }}>
                                    {screenshots.map((url, i) => (
                                      <img
                                        key={i}
                                        src={url}
                                        alt={`Screenshot ${i + 1}`}
                                        onClick={() => setFullScreenshot(url)}
                                        style={{
                                          height: '120px',
                                          borderRadius: '0.375rem',
                                          border: '1px solid #e5e7eb',
                                          cursor: 'pointer',
                                          flexShrink: 0,
                                        }}
                                      />
                                    ))}
                                  </div>
                                </Box>
                              )}

                              {/* Log */}
                              {logContent && (
                                <Box>
                                  <Text sx={{ fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: '600', marginBottom: '0.5rem' }}>
                                    Verifier Log
                                  </Text>
                                  <pre style={{
                                    backgroundColor: '#1f2937',
                                    color: '#e5e7eb',
                                    padding: '1rem',
                                    borderRadius: '0.375rem',
                                    fontSize: '0.75rem',
                                    lineHeight: '1.5',
                                    maxHeight: '300px',
                                    overflow: 'auto',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-all',
                                    margin: 0,
                                  }}>
                                    {logContent}
                                  </pre>
                                </Box>
                              )}
                            </Box>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </Box>
      )}

      {/* Full-size screenshot modal */}
      {fullScreenshot && (
        <div
          onClick={() => setFullScreenshot(null)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            cursor: 'pointer',
          }}
        >
          <img
            src={fullScreenshot}
            alt="Full-size screenshot"
            style={{
              maxWidth: '90vw',
              maxHeight: '90vh',
              borderRadius: '0.5rem',
              boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
            }}
          />
        </div>
      )}
    </Box>
  );
};

export default ValidationDashboard;
